import { useRef } from 'react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { Badge } from './Badge';
import { formatPaise } from '../lib/cn';
import { Printer, Download, FileText, Building2, Store, PhoneCall } from 'lucide-react';
import {
  MDD_SUPPORT_PHONE_DISPLAY,
  MDD_SUPPORT_PHONE,
  MDD_SUPPORT_EMAIL,
  MDD_GSTIN,
  MDD_PAN,
  MDD_TRADE_NAME,
  MDD_CONSTITUTION,
  MDD_REGISTERED_ADDRESS,
} from '../constants/contact';
import { QRCodeView } from './QRCodeView';

/** Registered seller details (trade name and business address). */
export const INVOICE_SELLER = {
  tradeName: MDD_TRADE_NAME,
  gstin: MDD_GSTIN,
  pan: MDD_PAN,
  constitution: MDD_CONSTITUTION,
  addressLines: MDD_REGISTERED_ADDRESS.lines,
  state: MDD_REGISTERED_ADDRESS.state,
  stateCode: MDD_REGISTERED_ADDRESS.stateCode,
};

export interface InvoiceLineItem {
  id: number;
  code: string;
  name: string;
  hsnCode?: string;
  gstRate?: number; // e.g. 0 (Nil), 5, 12, 18
  qty: number;
  pricePaise: number; // Unit price in paise
  discountPaise?: number; // Per item discount in paise
}

export interface TaxInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  shipmentId?: string;
  placedAt?: string;
  retailer?: {
    name: string;
    phone?: string | null;
    city?: string | null;
    addressLine?: string | null;
    gstNumber?: string | null;
    panNumber?: string | null;
  } | null;
  lines: InvoiceLineItem[];
  paymentMethod?: string;
  placeOfSupply?: string;
  supplyType?: 'INTRA_STATE' | 'INTER_STATE';
  reverseCharge?: boolean;
}

function numberToWords(amount: number): string {
  if (!amount || isNaN(amount) || amount <= 0) return 'Zero Rupees Only';
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (num: number): string => {
    if (num === 0) return '';
    if (num < 20) return units[num] + ' ';
    if (num < 100) return tens[Math.floor(num / 10)] + ' ' + convertLessThanThousand(num % 10);
    return units[Math.floor(num / 100)] + ' Hundred ' + convertLessThanThousand(num % 100);
  };

  let num = Math.floor(amount);
  let res = '';
  if (Math.floor(num / 10000000) > 0) {
    res += convertLessThanThousand(Math.floor(num / 10000000)) + 'Crore ';
    num %= 10000000;
  }
  if (Math.floor(num / 100000) > 0) {
    res += convertLessThanThousand(Math.floor(num / 100000)) + 'Lakh ';
    num %= 100000;
  }
  if (Math.floor(num / 1000) > 0) {
    res += convertLessThanThousand(Math.floor(num / 1000)) + 'Thousand ';
    num %= 1000;
  }
  if (num > 0) {
    res += convertLessThanThousand(num);
  }
  return 'Rupees ' + res.trim() + ' Only';
}

export function TaxInvoiceModal({
  open,
  onClose,
  orderId,
  shipmentId,
  placedAt,
  retailer,
  lines = [],
  paymentMethod = 'Cash on Delivery (COD)',
  placeOfSupply = 'KARNATAKA',
  supplyType = 'INTRA_STATE',
  reverseCharge = false,
}: TaxInvoiceModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);

  const invoiceNo = `INV-2026-${String(orderId).padStart(5, '0')}`;
  const resolvedShipmentId = shipmentId || `SHP-${String(orderId).padStart(8, '0')}`;
  const invoiceDate = placedAt ? new Date(placedAt).toLocaleDateString() : new Date().toLocaleDateString();

  // Helper to safely resolve unit price & discount
  const getItemPrice = (item: InvoiceLineItem) => {
    const p = Number(item.pricePaise);
    return !isNaN(p) && p > 0 ? p : 0;
  };

  const getItemQty = (item: InvoiceLineItem) => {
    const q = Number(item.qty);
    return !isNaN(q) && q > 0 ? q : 1;
  };

  const getItemDiscount = (item: InvoiceLineItem) => {
    const d = Number(item.discountPaise || 0);
    return !isNaN(d) && d > 0 ? d : 0;
  };

  // Safe Line Item & Aggregate Calculations using each item's actual GST %
  let grossTaxablePaise = 0;
  let totalDiscountPaise = 0;
  let baseTaxablePaise = 0;
  const gstRatesSet = new Set<number>();

  lines.forEach((item) => {
    const price = getItemPrice(item);
    const qty = getItemQty(item);
    const disc = getItemDiscount(item);
    const lineGross = price * qty;
    const lineDisc = disc * qty;
    const lineNet = Math.max(0, lineGross - lineDisc);
    const gstRate = item.gstRate !== undefined && item.gstRate !== null && !isNaN(Number(item.gstRate))
      ? Number(item.gstRate)
      : 5;
    gstRatesSet.add(gstRate);
    const lineBase = gstRate > 0 ? Math.round(lineNet / (1 + gstRate / 100)) : lineNet;

    grossTaxablePaise += lineGross;
    totalDiscountPaise += lineDisc;
    baseTaxablePaise += lineBase;
  });

  const netTaxablePaise = Math.max(0, grossTaxablePaise - totalDiscountPaise);
  const totalGstPaise = Math.max(0, netTaxablePaise - baseTaxablePaise);
  const cgstPaise = Math.round(totalGstPaise / 2);
  const sgstPaise = totalGstPaise - cgstPaise;
  const uniqueGstRates = Array.from(gstRatesSet);
  const halfRateLabel =
    uniqueGstRates.length === 1
      ? `${Number((uniqueGstRates[0] / 2).toFixed(2))}%`
      : "As Applicable";

  const grandTotalPaise = netTaxablePaise;
  const grandTotalRupees = Math.round(grandTotalPaise / 100);

  const handlePrint = () => {
    const printContent = printableRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tax Invoice - ${invoiceNo}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 5mm 8mm;
            }
            * {
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              color: #0f172a;
              background: #ffffff;
              margin: 0;
              padding: 0;
              font-size: 10.5px;
              line-height: 1.25;
            }
            img.logo-img {
              max-height: 36px !important;
              width: auto !important;
              max-width: 140px !important;
              object-fit: contain !important;
              display: block !important;
            }
            img.qr-img {
              width: 84px !important;
              height: 84px !important;
              max-width: 84px !important;
              max-height: 84px !important;
              object-fit: contain !important;
              display: block !important;
            }
            .flex { display: flex !important; }
            .flex-col { display: flex !important; flex-direction: column !important; }
            .items-center { align-items: center !important; }
            .items-start { align-items: flex-start !important; }
            .items-end { align-items: flex-end !important; }
            .justify-between { justify-content: space-between !important; }
            .justify-center { justify-content: center !important; }
            .gap-1 { gap: 3px !important; }
            .gap-1\.5 { gap: 5px !important; }
            .gap-2 { gap: 8px !important; }
            .gap-2\.5 { gap: 10px !important; }
            .gap-3 { gap: 12px !important; }
            .gap-4 { gap: 16px !important; }
            .grid { display: grid !important; }
            .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .sm\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .space-y-1 > * + * { margin-top: 2px !important; }
            .space-y-2 > * + * { margin-top: 5px !important; }
            .space-y-3 > * + * { margin-top: 8px !important; }
            .space-y-4 > * + * { margin-top: 12px !important; }
            .p-2\.5 { padding: 5px 8px !important; }
            .p-3 { padding: 7px !important; }
            .p-3\.5 { padding: 9px !important; }
            .p-4 { padding: 10px !important; }
            .p-6 { padding: 14px !important; }
            .py-4 { padding-top: 10px !important; padding-bottom: 10px !important; }
            .pb-3 { padding-bottom: 6px !important; }
            .pb-4 { padding-bottom: 10px !important; }
            .pt-2 { padding-top: 5px !important; }
            .pt-4 { padding-top: 10px !important; }
            .px-3 { padding-left: 8px !important; padding-right: 8px !important; }
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important; }
            .border { border: 1px solid #cbd5e1 !important; }
            .border-b { border-bottom: 1px solid #cbd5e1 !important; }
            .border-t { border-top: 1px solid #cbd5e1 !important; }
            .border-dashed { border-style: dashed !important; }
            .rounded { border-radius: 4px !important; }
            .rounded-lg { border-radius: 6px !important; }
            .rounded-xl { border-radius: 8px !important; }
            .bg-muted\/20 { background-color: #f8fafc !important; }
            .bg-muted\/30 { background-color: #f1f5f9 !important; }
            .bg-muted\/10 { background-color: #f8fafc !important; }
            .text-xs { font-size: 10px !important; }
            .text-2xs { font-size: 8.5px !important; }
            .text-sm { font-size: 11.5px !important; }
            .text-lg { font-size: 14px !important; }
            .text-base { font-size: 12px !important; }
            .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important; }
            .font-medium { font-weight: 500 !important; }
            .font-semibold { font-weight: 600 !important; }
            .font-bold { font-weight: 700 !important; }
            .font-extrabold { font-weight: 800 !important; }
            .font-black { font-weight: 900 !important; }
            .text-muted-foreground { color: #64748b !important; }
            .text-foreground { color: #0f172a !important; }
            .text-primary { color: #0284c7 !important; }
            .text-emerald-600 { color: #059669 !important; }
            .text-right { text-align: right !important; }
            .text-center { text-align: center !important; }
            .italic { font-style: italic !important; }
            .uppercase { text-transform: uppercase !important; }
            .tracking-wider { letter-spacing: 0.04em !important; }
            table { width: 100% !important; border-collapse: collapse !important; margin-top: 4px !important; margin-bottom: 4px !important; }
            th, td { border: 1px solid #cbd5e1 !important; padding: 4px 6px !important; font-size: 10px !important; }
            th { background-color: #f1f5f9 !important; font-weight: 700 !important; color: #334155 !important; }
            .h-8 { height: 26px !important; }
            .w-8 { width: 26px !important; }
            .shrink-0 { flex-shrink: 0 !important; }
            .relative { position: relative !important; }
            .overflow-hidden { overflow: hidden !important; }
            svg { display: inline-block !important; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadHtml = () => {
    const content = printableRef.current?.innerHTML;
    if (!content) return;

    const htmlString = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>Tax Invoice ${invoiceNo}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; color: #1e293b; max-width: 800px; margin: 0 auto; font-size: 11px; }
            img { max-height: 38px; width: auto; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; }
            th, td { border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; font-size: 10.5px; }
            th { background-color: #f1f5f9; }
            .text-right { text-align: right; }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;

    const blob = new Blob([htmlString], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${invoiceNo}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <div className="flex justify-between items-center pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-foreground">Tax Invoice & GST Receipt</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadHtml} className="gap-1.5 text-xs">
            <Download className="h-4 w-4" /> Download HTML/PDF
          </Button>
          <Button variant="primary" size="sm" onClick={handlePrint} className="gap-1.5 text-xs font-bold">
            <Printer className="h-4 w-4" /> Print Tax Invoice
          </Button>
        </div>
      </div>

      {/* Printable Invoice Container */}
      <div className="py-4 overflow-y-auto max-h-[75vh]">
        <div
          ref={printableRef}
          className="p-5 bg-card text-foreground rounded-xl border border-border shadow-xs space-y-4 text-xs"
        >
          {/* Header Bar with Logo & e-Invoice IRN QR Code */}
          <div className="flex justify-between items-start border-b border-border pb-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <img src="/my-daily-direct-logo.jpg" alt="MyDailyDirect" className="logo-img h-9 w-auto object-contain rounded" />
                <span className="text-base font-extrabold text-foreground">MyDailyDirect</span>
              </div>
              <p className="text-muted-foreground text-xs mt-0.5 font-semibold">{INVOICE_SELLER.tradeName}</p>
              {INVOICE_SELLER.addressLines.map((line) => (
                <p key={line} className="text-muted-foreground text-2xs">{line}</p>
              ))}
              {INVOICE_SELLER.gstin && (
                <p className="text-muted-foreground text-2xs font-mono">GSTIN: {INVOICE_SELLER.gstin} | PAN: {INVOICE_SELLER.pan}</p>
              )}
            </div>

            {/* Invoice Details & QR Verification Code */}
            <div className="flex items-start gap-4">
              <div className="text-right space-y-0.5">
                <Badge variant="default" className="text-xs px-2.5 py-0.5 font-extrabold uppercase tracking-wider">
                  TAX INVOICE
                </Badge>
                <div className="text-xs font-extrabold text-foreground font-mono mt-1">Invoice ID: {invoiceNo}</div>
                <div className="text-2xs text-muted-foreground font-mono">Shipment ID: {resolvedShipmentId}</div>
                <div className="text-2xs text-muted-foreground font-mono">Order Ref: ODR-{String(orderId).padStart(6, '0')}</div>
                <p className="text-2xs text-muted-foreground">Invoice Date: <span className="font-semibold text-foreground">{invoiceDate}</span></p>
                <p className="text-2xs text-muted-foreground">Place of Supply: <span className="font-semibold text-foreground">{placeOfSupply}</span></p>
                <p className="text-2xs text-muted-foreground">Supply Type: <span className="font-mono font-bold text-primary">{supplyType}</span></p>
              </div>

              {/* Scannable e-Invoice IRN & UPI Payment QR Code */}
              <div className="border border-border p-1.5 rounded bg-white flex flex-col items-center justify-center shrink-0 shadow-2xs">
                <QRCodeView
                  value={`upi://pay?pa=mdd.b2b@icici&pn=My+Daily+Direct&am=${(grandTotalPaise / 100).toFixed(2)}&tr=${invoiceNo}&cu=INR&tn=Invoice+${invoiceNo}+IRN+e7b2c98d4f1a`}
                  size={84}
                  className="qr-img"
                  alt={`e-Invoice QR ${invoiceNo}`}
                />
                <span className="text-[7.5px] font-mono text-slate-800 font-bold mt-1 uppercase tracking-tighter">
                  e-Invoice IRN Verified
                </span>
              </div>
            </div>
          </div>

          {/* Seller & Buyer Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Seller Info */}
            <div className="p-2.5 rounded-lg border border-border bg-muted/20 space-y-0.5 text-2xs">
              <span className="font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Building2 className="h-3 w-3 text-primary" /> Bill From / Ship From
              </span>
              <p className="font-bold text-foreground text-xs">{INVOICE_SELLER.tradeName}</p>
              {INVOICE_SELLER.addressLines.map((line) => (
                <p key={line} className="text-muted-foreground">{line}</p>
              ))}
              {INVOICE_SELLER.gstin && (
                <p className="text-muted-foreground font-mono">GSTIN: {INVOICE_SELLER.gstin} | PAN: {INVOICE_SELLER.pan}</p>
              )}
              <p className="text-muted-foreground font-mono">State: {INVOICE_SELLER.state} (Code: {INVOICE_SELLER.stateCode})</p>
            </div>

            {/* Buyer Kirana Info */}
            <div className="p-2.5 rounded-lg border border-border bg-muted/20 space-y-0.5 text-2xs">
              <span className="font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Store className="h-3 w-3 text-primary" /> Bill To / Ship To (Buyer Shop)
              </span>
              <p className="font-bold text-foreground text-xs">{retailer?.name || `Kirana Retailer #${orderId}`}</p>
              <p className="text-muted-foreground">{retailer?.addressLine || 'Main Market Road'}, {retailer?.city || 'Bangalore'}</p>
              {retailer?.gstNumber ? (
                <p className="text-muted-foreground font-mono">GSTIN: {retailer.gstNumber}</p>
              ) : (
                <p className="text-muted-foreground">GST: Unregistered (Composition/B2C)</p>
              )}
              <p className="text-muted-foreground">Phone: {retailer?.phone || '+91 98000 12345'}</p>
            </div>
          </div>

          {/* Itemized Invoice Table */}
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted text-muted-foreground font-bold border-b border-border">
                <tr>
                  <th className="p-1.5 w-[4%] text-center">#</th>
                  <th className="p-1.5 w-[12%]">SKU Code</th>
                  <th className="p-1.5 w-[26%]">Description</th>
                  <th className="p-1.5 w-[8%] text-center">HSN</th>
                  <th className="p-1.5 w-[5%] text-center">Qty</th>
                  <th className="p-1.5 w-[9%] text-right">Rate</th>
                  <th className="p-1.5 w-[8%] text-right">Disc.</th>
                  <th className="p-1.5 w-[10%] text-right">Net Taxable</th>
                  <th className="p-1.5 w-[8%] text-center">Tax Type</th>
                  <th className="p-1.5 w-[10%] text-right">Total Amt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-2xs">
                {lines.map((item, idx) => {
                  const price = getItemPrice(item);
                  const qty = getItemQty(item);
                  const disc = getItemDiscount(item);
                  const lineGross = price * qty;
                  const lineDiscTotal = disc * qty;
                  const lineNetTaxable = Math.max(0, lineGross - lineDiscTotal);
                  const gstRate = item.gstRate !== undefined ? item.gstRate : 5;

                  return (
                    <tr key={item.id || idx} className="hover:bg-muted/30">
                      <td className="p-1.5 text-center text-muted-foreground">{idx + 1}</td>
                      <td className="p-1.5 font-mono font-medium">{item.code}</td>
                      <td className="p-1.5 font-semibold text-foreground">{item.name}</td>
                      <td className="p-1.5 text-center font-mono text-muted-foreground">{item.hsnCode || '11010000'}</td>
                      <td className="p-1.5 text-center font-bold">{qty}</td>
                      <td className="p-1.5 text-right font-mono">{formatPaise(price)}</td>
                      <td className="p-1.5 text-right font-mono text-amber-600 dark:text-amber-400">
                        {disc > 0 ? formatPaise(disc) : '—'}
                      </td>
                      <td className="p-1.5 text-right font-mono font-medium">{formatPaise(lineNetTaxable)}</td>
                      <td className="p-1.5 text-center font-mono font-semibold">
                        {gstRate <= 0
                          ? '0% (Nil)'
                          : `CGST ${Number((gstRate / 2).toFixed(2))}% + SGST ${Number((gstRate / 2).toFixed(2))}%`}
                      </td>
                      <td className="p-1.5 text-right font-bold font-mono text-foreground">
                        {formatPaise(lineNetTaxable)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary, Calculations & Bank Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div className="space-y-2 text-2xs">
              <div className="p-2 rounded-lg border border-border bg-muted/10 space-y-0.5">
                <span className="font-bold uppercase tracking-wider text-muted-foreground">Amount in Words</span>
                <p className="font-bold text-foreground italic text-xs">{numberToWords(grandTotalRupees)}</p>
              </div>

              <div className="p-2 rounded-lg border border-border bg-muted/10 space-y-1">
                <span className="font-bold uppercase tracking-wider text-muted-foreground block">Terms of Credit & NEFT Bank Details</span>
                <p className="text-muted-foreground">
                  Bill amount to be paid by <strong>{paymentMethod}</strong>.
                  Payments by NEFT to: <strong>{INVOICE_SELLER.tradeName}</strong>.
                </p>
                <div className="flex items-center gap-3 pt-0.5 font-mono text-[9px] text-muted-foreground border-t border-border/60">
                  <span>IFSC: <strong>HDFC0000240</strong></span>
                  <span>A/C: <strong>MDD{MDD_SUPPORT_PHONE}</strong></span>
                  <span>Reverse Charge: <strong>{reverseCharge ? 'YES' : 'NO'}</strong></span>
                </div>
              </div>
            </div>

            {/* Summary Calculations Box */}
            <div className="p-2.5 rounded-xl border border-border bg-muted/30 space-y-1 text-2xs">
              <div className="flex justify-between text-muted-foreground">
                <span>Gross Taxable Amount</span>
                <span className="font-mono font-medium">{formatPaise(grossTaxablePaise)}</span>
              </div>
              {totalDiscountPaise > 0 && (
                <div className="flex justify-between text-amber-600 dark:text-amber-400">
                  <span>Total Discount</span>
                  <span className="font-mono font-semibold">- {formatPaise(totalDiscountPaise)}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>Net Taxable Amount</span>
                <span className="font-mono font-medium">{formatPaise(baseTaxablePaise)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>CGST ({halfRateLabel})</span>
                <span className="font-mono font-medium">{formatPaise(cgstPaise)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>SGST ({halfRateLabel})</span>
                <span className="font-mono font-medium">{formatPaise(sgstPaise)}</span>
              </div>
              <div className="border-t border-border pt-1 flex justify-between items-center text-xs font-extrabold text-foreground">
                <span>Grand Total</span>
                <span className="text-sm text-primary font-black font-mono">{formatPaise(grandTotalPaise)}</span>
              </div>
            </div>
          </div>

          {/* Footer Statutory Declarations & Signatory Stamp */}
          <div className="border-t border-border pt-2.5 flex flex-col sm:flex-row justify-between items-end gap-3 text-[9px] text-muted-foreground">
            <div className="space-y-0.5 max-w-lg">
              <p className="font-bold text-foreground">DECLARATION & TERMS:</p>
              <p>We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct. Is tax payable on reverse charge basis: <strong>NO</strong>.</p>
              <p className="flex items-center gap-1 text-[8.5px]">
                <PhoneCall className="h-2.5 w-2.5 text-primary" /> Support: Contact MyDailyDirect Customer Care at <strong>{MDD_SUPPORT_PHONE_DISPLAY}</strong> or email <strong>{MDD_SUPPORT_EMAIL}</strong>.
              </p>
            </div>
            <div className="text-right space-y-0.5 shrink-0 border-t sm:border-t-0 pt-1 sm:pt-0">
              <div className="h-7 w-28 border border-dashed border-border rounded flex items-center justify-center text-[8px] text-muted-foreground font-mono ml-auto">
                Authorised Signatory
              </div>
              <p className="font-bold text-foreground text-[9.5px]">For {INVOICE_SELLER.tradeName}</p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
